import { useState } from "react";
import React from "react";
import Icon from "./Icon";
import ConfirmDelete from "./ConfirmDelete";

const COMM_TYPES = {
  phone:   { icon: "phone",    label: "Téléphone",     color: "#3b82f6" },
  email:   { icon: "mail",     label: "Courriel",      color: "#8b5cf6" },
  meeting: { icon: "users",    label: "Rencontre",     color: "#10b981" },
  note:    { icon: "file",     label: "Note",          color: "#f59e0b" },
  portal:  { icon: "monitor",  label: "Portail",       color: "#6366f1" },
};

function CommIcon({ type, size=16 }) {
  const cfg = COMM_TYPES[type] || COMM_TYPES.note;
  return (
    <span style={{
      width: size+8, height: size+8, borderRadius: "50%",
      background: cfg.color+"22", color: cfg.color,
      display:"inline-flex", alignItems:"center", justifyContent:"center", flexShrink:0,
    }}>
      <Icon name={cfg.icon} size={size}/>
    </span>
  );
}

function CommunicationsTimeline({ comms, customers, estimates, jobs, teamMembers, onAdd, onEdit, onDelete, contextCustomerId, contextEstimateId, contextJobId }) {
  const today = new Date().toISOString().split("T")[0];
  const blank = { type:"phone", direction:"outbound", subject:"", body:"", date:today, responsible:"", customerId:contextCustomerId||"", estimateId:contextEstimateId||"", jobId:contextJobId||"", isTask:false, taskDueDate:"", taskDone:false };
  const [form, setForm] = useState(null);
  const [tab, setTab] = useState("all"); // "all" | "tasks"
  const [confirmDel, setConfirmDel] = useState(null);
  const [replyState, setReplyState] = useState({}); // keyed by comm id

  const sendReply = async (c) => {
    const text = (replyState[c.id]?.text || "").trim();
    if (!text) return;
    setReplyState(p => ({ ...p, [c.id]: { ...p[c.id], sending: true } }));
    await onAdd({
      type: "portal", direction: "outbound",
      subject: "Réponse", body: text,
      customerId: c.customerId || contextCustomerId || "",
      estimateId: c.estimateId || contextEstimateId || "",
      jobId: c.jobId || contextJobId || "",
      date: today, isTask: false, taskDone: false,
    });
    setReplyState(p => ({ ...p, [c.id]: { open: false, text: "", sent: true, sending: false } }));
    setTimeout(() => setReplyState(p => ({ ...p, [c.id]: { open: false, text: "" } })), 2000);
  };

  const filtered = comms.filter(c => {
    if(contextCustomerId && c.customerId !== contextCustomerId) return false;
    if(contextEstimateId && c.estimateId !== contextEstimateId) return false;
    if(contextJobId && c.jobId !== contextJobId) return false;
    if(tab === "tasks") return c.isTask;
    return true;
  });

  const openTasks  = filtered.filter(c=>c.isTask && !c.taskDone);
  const doneTasks  = filtered.filter(c=>c.isTask && c.taskDone);
  const activities = filtered.filter(c=>!c.isTask);

  const save = async () => {
    if(!form) return;
    if(form.id) await onEdit(form.id, form);
    else await onAdd(form);
    setForm(null);
  };

  const toggleDone = async (c) => {
    await onEdit(c.id, { ...c, taskDone: !c.taskDone, taskDoneDate: !c.taskDone ? today : "" });
  };

  const customerName = (id) => { const c=customers.find(x=>x.id===id); return c?`${c.firstName} ${c.lastName}`:""; };
  const estimateTitle = (id) => estimates.find(x=>x.id===id)?.title||"";
  const jobTitle = (id) => jobs.find(x=>x.id===id)?.title||"";

  const Section = ({ title, items, showEmpty=false }) => {
    if(!items.length && !showEmpty) return null;
    return (
      <div style={{marginBottom:20}}>
        <div style={{fontSize:10,fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:"var(--text-3)",marginBottom:8,paddingLeft:4}}>{title}</div>
        {items.length===0 && <div style={{fontSize:12,color:"var(--text-3)",padding:"12px 4px"}}>Aucun élément</div>}
        {items.map(c=>{
          const isInboundPortal = c.type==="portal" && c.direction==="inbound";
          const rs = replyState[c.id] || {};
          return (
            <React.Fragment key={c.id}>
              <div style={{display:"flex",gap:10,padding:"10px 4px",borderBottom:(!isInboundPortal||!rs.open)?"1px solid var(--border)":"none",alignItems:"flex-start"}}>
                <div style={{paddingTop:2}}><CommIcon type={c.type} size={14}/></div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                    <span style={{fontWeight:600,fontSize:13,color:c.isTask&&!c.taskDone?"var(--red)":"var(--text)"}}>{c.subject||"—"}</span>
                    {c.isTask&&<span className={`badge ${c.taskDone?"badge-green":"badge-amber"}`}>{c.taskDone?"✓ Terminé":"Tâche"}</span>}
                    {c.direction==="inbound"&&<span className="badge badge-blue">Entrant</span>}
                  </div>
                  {c.body&&<div style={{fontSize:12,color:"var(--text-2)",marginTop:3,lineHeight:1.5,whiteSpace:"pre-wrap"}}>{c.body}</div>}
                  <div style={{display:"flex",gap:12,marginTop:5,fontSize:11,color:"var(--text-3)",flexWrap:"wrap"}}>
                    <span>{c.date}</span>
                    {c.isTask&&c.taskDueDate&&<span style={{color:c.taskDone?"var(--green)":c.taskDueDate<today?"var(--red)":"var(--text-3)"}}>Échéance: {c.taskDueDate}</span>}
                    {c.responsible&&<span>👤 {c.responsible}</span>}
                    {!contextCustomerId&&c.customerId&&<span>🏠 {customerName(c.customerId)}</span>}
                    {!contextEstimateId&&c.estimateId&&<span>📋 {estimateTitle(c.estimateId)}</span>}
                    {!contextJobId&&c.jobId&&<span>🔨 {jobTitle(c.jobId)}</span>}
                  </div>
                </div>
                <div style={{display:"flex",gap:4,flexShrink:0}}>
                  {c.isTask&&<button className="btn-icon" title={c.taskDone?"Rouvrir":"Marquer terminé"} onClick={()=>toggleDone(c)} style={{color:c.taskDone?"var(--green)":"var(--text-3)"}}><Icon name={c.taskDone?"x":"check"} size={13}/></button>}
                  {isInboundPortal ? (
                    rs.sent ? (
                      <span style={{fontSize:11,color:"#16a34a",fontWeight:600,padding:"2px 4px"}}>✓ Envoyé</span>
                    ) : (
                      <button className="btn-icon" title="Répondre / Reply" onClick={()=>setReplyState(p=>({...p,[c.id]:{...p[c.id],open:!rs.open,text:rs.text||""}}))} style={{color:"#6366f1"}}>
                        <Icon name="mail" size={13}/>
                      </button>
                    )
                  ) : (
                    <button className="btn-icon" title="Modifier" onClick={()=>setForm({...c})}><Icon name="edit" size={13}/></button>
                  )}
                  <button className="btn-icon danger" title="Supprimer" onClick={()=>setConfirmDel(c)}><Icon name="trash" size={13}/></button>
                </div>
              </div>
              {isInboundPortal&&rs.open&&(
                <div style={{padding:"8px 4px 12px 32px",borderBottom:"1px solid var(--border)",background:"#f8f8ff"}}>
                  <div style={{display:"flex",gap:8,alignItems:"flex-end"}}>
                    <textarea
                      className="form-input"
                      style={{flex:1,minHeight:52,resize:"vertical",fontSize:12}}
                      placeholder="Répondre / Reply…"
                      value={rs.text||""}
                      onChange={e=>setReplyState(p=>({...p,[c.id]:{...p[c.id],text:e.target.value}}))}
                      onKeyDown={e=>{ if(e.key==="Enter"&&(e.metaKey||e.ctrlKey)) sendReply(c); }}
                      autoFocus
                    />
                    <div style={{display:"flex",flexDirection:"column",gap:4,flexShrink:0}}>
                      <button className="btn btn-primary btn-sm" disabled={rs.sending||!(rs.text||"").trim()} onClick={()=>sendReply(c)}>
                        {rs.sending?"…":"Envoyer"}
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={()=>setReplyState(p=>({...p,[c.id]:{...p[c.id],open:false}}))}>
                        Annuler
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      {/* Toolbar */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14,gap:8}}>
        <div style={{display:"flex",gap:4}}>
          {[["all","Tout"],["tasks","Tâches"]].map(([k,l])=>(
            <button key={k} className={`btn btn-sm ${tab===k?"btn-primary":"btn-secondary"}`} onClick={()=>setTab(k)}>{l}</button>
          ))}
        </div>
        <div style={{display:"flex",gap:6}}>
          <button className="btn btn-secondary btn-sm" onClick={()=>setForm({...blank,isTask:false})}><Icon name="plus" size={12}/>Activité</button>
          <button className="btn btn-primary btn-sm" onClick={()=>setForm({...blank,isTask:true})}><Icon name="plus" size={12}/>Tâche</button>
        </div>
      </div>

      {/* Timeline */}
      {tab==="all" ? (
        <>
          <Section title={`Tâches ouvertes (${openTasks.length})`} items={openTasks} showEmpty/>
          <Section title={`Activités (${activities.length})`} items={activities} showEmpty/>
          {doneTasks.length>0&&<Section title={`Tâches terminées (${doneTasks.length})`} items={doneTasks}/>}
        </>
      ) : (
        <>
          <Section title="À faire" items={openTasks} showEmpty/>
          <Section title="Terminées" items={doneTasks}/>
        </>
      )}

      {/* Add/Edit modal */}
      {form&&(
        <div className="modal-backdrop" style={{zIndex:250}} onClick={e=>e.target===e.currentTarget&&setForm(null)}>
          <div className="modal">
            <div className="modal-header">
              <div>
                <div className="modal-title">{form.id?(form.isTask?"Modifier tâche":"Modifier activité"):(form.isTask?"Nouvelle tâche":"Nouvelle activité")}</div>
              </div>
              <button className="btn-icon" title="Fermer" onClick={()=>setForm(null)}><Icon name="x"/></button>
            </div>
            <div className="modal-body">
              <div className="form-grid form-grid-2">
                {/* Type icons */}
                <div className="form-group" style={{gridColumn:"1/-1"}}>
                  <label>Type</label>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    {Object.entries(COMM_TYPES).filter(([k])=>k!=="portal").map(([k,v])=>(
                      <button key={k} onClick={()=>setForm(p=>({...p,type:k}))} style={{
                        display:"flex",flexDirection:"column",alignItems:"center",gap:4,padding:"8px 14px",
                        border:`2px solid ${form.type===k?v.color:"var(--border)"}`,
                        borderRadius:"var(--radius-sm)",background:form.type===k?v.color+"15":"var(--bg)",
                        cursor:"pointer",color:form.type===k?v.color:"var(--text-3)",fontSize:11,fontWeight:600,
                      }}>
                        <Icon name={v.icon} size={18}/>
                        {v.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Direction (only for non-task) */}
                {!form.isTask&&(
                  <div className="form-group">
                    <label>Direction</label>
                    <select className="form-select" value={form.direction||"outbound"} onChange={e=>setForm(p=>({...p,direction:e.target.value}))}>
                      <option value="outbound">Sortant</option>
                      <option value="inbound">Entrant</option>
                    </select>
                  </div>
                )}

                {/* Date */}
                <div className="form-group">
                  <label>{form.isTask?"Date":"Date"}</label>
                  <input className="form-input" type="date" value={form.date||today} onChange={e=>setForm(p=>({...p,date:e.target.value}))}/>
                </div>

                {/* Task due date */}
                {form.isTask&&(
                  <div className="form-group">
                    <label>Date d'échéance</label>
                    <input className="form-input" type="date" value={form.taskDueDate||""} onChange={e=>setForm(p=>({...p,taskDueDate:e.target.value}))}/>
                  </div>
                )}

                {/* Subject */}
                <div className="form-group" style={{gridColumn:"1/-1"}}>
                  <label>Sujet *</label>
                  <input className="form-input" value={form.subject||""} onChange={e=>setForm(p=>({...p,subject:e.target.value}))} placeholder={form.isTask?"Ex: Envoyer soumission révisée":"Ex: Appel de suivi"}/>
                </div>

                {/* Responsible */}
                <div className="form-group">
                  <label>Responsable</label>
                  {teamMembers.length>0 ? (
                    <select className="form-select" value={form.responsible||""} onChange={e=>setForm(p=>({...p,responsible:e.target.value}))}>
                      <option value="">— Sélectionner —</option>
                      {teamMembers.map(m=><option key={m.id} value={m.name}>{m.name}</option>)}
                      <option value="__manual__">Autre...</option>
                    </select>
                  ) : (
                    <input className="form-input" value={form.responsible||""} onChange={e=>setForm(p=>({...p,responsible:e.target.value}))} placeholder="Nom..."/>
                  )}
                </div>
                {form.responsible==="__manual__"&&(
                  <div className="form-group">
                    <label>Nom (manuel)</label>
                    <input className="form-input" autoFocus value={form._manualResp||""} onChange={e=>setForm(p=>({...p,_manualResp:e.target.value,responsible:e.target.value}))}/>
                  </div>
                )}

                {/* Link to customer/estimate/job */}
                {!contextCustomerId&&(
                  <div className="form-group">
                    <label>Client</label>
                    <select className="form-select" value={form.customerId||""} onChange={e=>setForm(p=>({...p,customerId:e.target.value}))}>
                      <option value="">—</option>
                      {customers.map(c=><option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
                    </select>
                  </div>
                )}
                {!contextEstimateId&&(
                  <div className="form-group">
                    <label>Soumission</label>
                    <select className="form-select" value={form.estimateId||""} onChange={e=>setForm(p=>({...p,estimateId:e.target.value}))}>
                      <option value="">—</option>
                      {estimates.map(e=><option key={e.id} value={e.id}>{e.title}</option>)}
                    </select>
                  </div>
                )}

                {/* Body/notes */}
                <div className="form-group" style={{gridColumn:"1/-1"}}>
                  <label>Notes</label>
                  <textarea className="form-textarea" style={{minHeight:80}} value={form.body||""} onChange={e=>setForm(p=>({...p,body:e.target.value}))} placeholder="Détails..."/>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setForm(null)}>Annuler</button>
              <button className="btn btn-primary" onClick={save}>Sauvegarder</button>
            </div>
          </div>
        </div>
      )}

      {confirmDel&&<ConfirmDelete message={`Supprimer "${confirmDel.subject}"?`} onConfirm={()=>{onDelete(confirmDel.id);setConfirmDel(null);}} onCancel={()=>setConfirmDel(null)}/>}
    </div>
  );
}

export default CommunicationsTimeline;
